import { useEffect, useState } from 'react';
import { api } from '@/lib/axios';
import { apiErrorText } from '@/lib/api-error';
import {
  FeldStatus,
  FeldStatusZeichen,
  mitStand,
  useAenderung,
  useEntwurf,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { getRefSchemas } from '../api/time-master-data.api';
import type {
  CustomFieldSpec,
  CustomFieldsSchema,
  CustomFieldType,
  RefEntityKind,
  RefSchemasResponse,
} from '../types/time-master-data.types';
import { bestaetige } from '@/lib/dialoge';

/**
 * AP-09-C — JSON-Schema-Editor für CustomFields aller 5 Ref-Entitäten.
 *
 * Der Editor arbeitet pro Entität mit einem einfachen Tabellen-Editor (kein
 * roher JSON-Textarea), damit Admins nicht versehentlich ein ungültiges Schema
 * speichern.
 *
 * AP-36 Phase C: **Zeile = Gruppe.** Eine Feldzeile speichert beim Verlassen,
 * als Operation auf dem aktuellen Stand des Schemas, erkannt am gespeicherten
 * Feldnamen (8.3 a). Eine neue Zeile wird beim Verlassen angelegt, sobald sie
 * gültig ist. „Unbekannte Felder abweisen", Reihenfolge und Entfernen gehen
 * sofort. Den Knopf „Speichern" je Entität gibt es nicht mehr.
 *
 * Löschen des Schemas (Button „Eigene Felder entfernen") setzt den Server-Wert auf
 * `null` — danach werden alle CustomFields für diese Entität abgewiesen.
 */

const TABS: Array<{ key: RefEntityKind; label: string; helper: string }> = [
  { key: 'project',  label: 'Projekte',         helper: 'Eigene Felder je Projekt (z. B. Kostenstelle, Auftragsart)' },
  { key: 'position', label: 'Positionen',       helper: 'Eigene Felder je Position (z. B. Gewerk, Abrechnungsart)' },
  { key: 'step',     label: 'Arbeitsschritte',  helper: 'Eigene Felder je Arbeitsschritt (z. B. Akkord, Stückzahl)' },
  { key: 'activity', label: 'Tätigkeiten',      helper: 'Eigene Felder je Tätigkeit (z. B. Zuschlagsgruppe)' },
  { key: 'userRef',  label: 'Mitarbeiter', helper: 'Eigene Felder je Mitarbeiter (z. B. Abteilung, Personalnummer)' },
];

const TYPE_OPTIONS: CustomFieldType[] = ['string', 'number', 'boolean', 'date', 'enum'];

function emptySpec(): CustomFieldSpec {
  return { name: '', type: 'string', required: false };
}

function validateSpec(field: CustomFieldSpec): string | null {
  if (!field.name || !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(field.name)) {
    return 'Feldname muss mit Buchstabe beginnen und darf nur a-z, A-Z, 0-9, _ enthalten.';
  }
  if (field.type === 'enum' && (!field.options || field.options.length === 0)) {
    return 'Enum-Felder brauchen mindestens eine Option.';
  }
  if (field.type === 'number') {
    if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
      return 'min darf nicht größer als max sein.';
    }
  }
  return null;
}

/** Das Schema einer Entität aus einem Stand — `null` heißt „keine eigenen Felder". */
const schemaAus = (daten: Datensatz | undefined, kind: RefEntityKind): CustomFieldsSchema | null =>
  (daten?.[kind] as CustomFieldsSchema | null | undefined) ?? null;

const felderAus = (daten: Datensatz | undefined, kind: RefEntityKind): CustomFieldSpec[] => schemaAus(daten, kind)?.fields ?? [];

/** Operation: die Felder einer Entität umbauen — ein fehlendes Schema entsteht dabei. */
const aendereSchema =
  (kind: RefEntityKind, umbau: (s: CustomFieldsSchema) => CustomFieldsSchema) => (aktuell: Datensatz) => ({
    [kind]: umbau(schemaAus(aktuell, kind) ?? { fields: [], strict: false }),
  });

export default function TimeRefSchemasPage() {
  const [daten, setDaten] = useState<RefSchemasResponse | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    getRefSchemas()
      .then((r) => aktiv && setDaten(r))
      .catch((e) => aktiv && setFehler(apiErrorText(e, 'Fehler beim Laden')));
    return () => {
      aktiv = false;
    };
  }, []);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'timeRefSchemas',
    bezeichnung: 'Eigene Felder der Stammdaten',
    daten: daten as Datensatz | null,
    schreibe: (patch, stand) => api.patch<Datensatz>('/time-master-data/schemas', patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: '/time-master-data/schemas', method: 'PATCH' },
  });

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Eigene Felder</h1>
        <p className="text-sm text-muted-foreground">
          Hier legst du fest, welche eigenen Felder erlaubt oder Pflicht sind.
          Sie werden beim Datenabgleich und bei der manuellen Pflege geprüft.
          Eine Zeile wird gespeichert, sobald du sie verlässt.
        </p>
      </header>

      {!daten && !fehler && <div className="text-sm text-muted-foreground">Wird geladen …</div>}
      {fehler && <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded px-3 py-2">{fehler}</div>}
      {daten && <Schemata stelle={stelle as Speicherstelle<unknown>} />}
    </div>
  );
}

function Schemata({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const daten = useStellenDaten(stelle);
  const [active, setActive] = useState<RefEntityKind>('project');
  const entfernen = useAenderung(stelle, 'timeRefSchemas:entfernen');
  const schema = schemaAus(daten, active);

  async function remove(kind: RefEntityKind) {
    if (!(await bestaetige('Diese eigenen Felder wirklich entfernen? Danach werden alle dazu gespeicherten Werte abgewiesen.', { ok: 'Entfernen', gefaehrlich: true }))) return;
    void entfernen.sende({ felder: [kind], patch: { [kind]: null }, wert: kind });
  }

  return (
    <>
      <nav className="flex gap-1 border-b">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px ' +
              (active === tab.key
                ? 'border-primary font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >
            {tab.label}
            {schemaAus(daten, tab.key) && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({felderAus(daten, tab.key).length})
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {TABS.find(t => t.key === active)?.helper}
        </p>

        <SchemaEditor key={active} stelle={stelle} kind={active} />

        <div className="flex items-center gap-2 pt-2 border-t">
          {schema && (
            <button
              type="button"
              onClick={() => void remove(active)}
              className="border border-red-200 text-red-700 rounded px-3 py-2 text-sm hover:bg-red-50"
            >
              Eigene Felder entfernen
            </button>
          )}
          <FeldStatusZeichen status={entfernen.status} />
          <span className="ml-auto text-xs text-muted-foreground">
            {felderAus(daten, active).length} Feld{felderAus(daten, active).length === 1 ? '' : 'er'}
          </span>
        </div>
        <FeldStatus status={entfernen.status} zeichen={false} />
      </div>
    </>
  );
}

// ─── SchemaEditor-Komponente ─────────────────────────────────────────────────

let neueZeile = 0;

function SchemaEditor({ stelle, kind }: { stelle: Speicherstelle<unknown>; kind: RefEntityKind }) {
  const daten = useStellenDaten(stelle);
  const schema = schemaAus(daten, kind);
  const fields = schema?.fields ?? [];
  const strikt = useAenderung(stelle, `${kind}:strict`);
  const reihe = useAenderung(stelle, `${kind}:reihenfolge`);
  // Neue Zeilen sind Entwürfe, bis sie beim Verlassen gültig angelegt werden.
  const [neue, setNeue] = useState<number[]>([]);

  function move(name: string, delta: -1 | 1) {
    void reihe.sende({
      felder: [kind],
      operation: aendereSchema(kind, (s) => {
        const next = s.fields.slice();
        const index = next.findIndex((f) => f.name === name);
        const target = index + delta;
        if (index < 0 || target < 0 || target >= next.length) return s;
        [next[index], next[target]] = [next[target], next[index]];
        return { ...s, fields: next };
      }),
    });
  }

  return (
    <div className="space-y-3">
      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!schema?.strict}
          onChange={e => {
            const an = e.target.checked;
            void strikt.sende({ felder: [kind], wert: 'strict', operation: aendereSchema(kind, (s) => ({ ...s, strict: an })) });
          }}
        />
        <span>
          <span className="flex items-center gap-1.5">
            Unbekannte Felder abweisen <FeldStatusZeichen status={strikt.status} />
          </span>
          <span className="block text-xs text-muted-foreground">
            Wenn aktiv, werden beim Datenabgleich zusätzliche, nicht festgelegte Felder abgelehnt.
          </span>
        </span>
      </label>
      <FeldStatus status={strikt.status} zeichen={false} />
      <FeldStatus status={reihe.status} zeichen={false} />

      <div className="overflow-x-auto border rounded">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-2 py-2 w-40">Name</th>
              <th className="text-left px-2 py-2 w-40">Label</th>
              <th className="text-left px-2 py-2 w-28">Typ</th>
              <th className="text-left px-2 py-2 w-20">Pflicht</th>
              <th className="text-left px-2 py-2">Einschränkungen</th>
              <th className="text-right px-2 py-2 w-32">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && neue.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4 text-muted-foreground">
                  Keine Felder definiert. Füge unten eines hinzu.
                </td>
              </tr>
            )}
            {fields.map((field, i) => (
              <SchemaZeile
                key={field.name}
                stelle={stelle}
                kind={kind}
                gespeichert={field}
                andereNamen={fields.filter((f) => f.name !== field.name).map((f) => f.name)}
                isFirst={i === 0}
                isLast={i === fields.length - 1}
                onMove={(delta) => move(field.name, delta)}
              />
            ))}
            {neue.map((nr) => (
              <SchemaZeile
                key={`neu-${nr}`}
                stelle={stelle}
                kind={kind}
                gespeichert={null}
                andereNamen={fields.map((f) => f.name)}
                isFirst
                isLast
                onAngelegt={() => setNeue((n) => n.filter((x) => x !== nr))}
                onVerwerfen={() => setNeue((n) => n.filter((x) => x !== nr))}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setNeue((n) => [...n, ++neueZeile])}
          className="text-xs border rounded px-3 py-1.5 hover:bg-accent"
        >
          + Feld hinzufügen
        </button>
      </div>
    </div>
  );
}

/** Eine Feldzeile: Entwurf, gespeichert beim Verlassen der Zeile (neu: angelegt). */
function SchemaZeile({
  stelle,
  kind,
  gespeichert,
  andereNamen,
  isFirst,
  isLast,
  onMove,
  onAngelegt,
  onVerwerfen,
}: {
  stelle: Speicherstelle<unknown>;
  kind: RefEntityKind;
  gespeichert: CustomFieldSpec | null;
  andereNamen: string[];
  isFirst: boolean;
  isLast: boolean;
  onMove?: (delta: -1 | 1) => void;
  onAngelegt?: () => void;
  onVerwerfen?: () => void;
}) {
  const neu = gespeichert === null;
  const alterName = gespeichert?.name;
  const [start] = useState<CustomFieldSpec>(() => gespeichert ?? emptySpec());
  const [neuId] = useState(() => ++neueZeile);
  const entwurf = useEntwurf<CustomFieldSpec>(stelle, {
    kennung: `${kind}:${alterName ?? `neu:${neuId}`}`,
    gespeichert: gespeichert ?? start,
    felder: [kind],
    label: gespeichert?.label ?? gespeichert?.name ?? 'Feld',
    pruefe: (f) => validateSpec(f) ?? (andereNamen.includes(f.name) ? `Feldname "${f.name}" doppelt.` : null),
    operation: (f) =>
      aendereSchema(kind, (s) => ({
        ...s,
        fields: neu ? (s.fields.some((x) => x.name === f.name) ? s.fields : [...s.fields, f]) : s.fields.map((x) => (x.name === alterName ? f : x)),
      })),
  });
  const aktion = useAenderung(stelle, `${kind}:${alterName ?? 'neu'}:aktion`);
  const field = entwurf.entwurf ?? start;
  const updateField = (patch: Partial<CustomFieldSpec>) => entwurf.setze((f) => ({ ...f, ...patch }));

  // Eine neue Zeile ist angelegt, sobald ihr Name in der gespeicherten Liste steht.
  useEffect(() => {
    if (neu && entwurf.zustand === 'gespeichert') onAngelegt?.();
  }, [neu, entwurf.zustand, onAngelegt]);

  async function removeField() {
    if (neu) {
      onVerwerfen?.();
      return;
    }
    if (!(await bestaetige(`Feld „${gespeichert!.label || gespeichert!.name}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true }))) return;
    void aktion.sende({ felder: [kind], operation: aendereSchema(kind, (s) => ({ ...s, fields: s.fields.filter((x) => x.name !== alterName) })) });
  }

  const status = entwurf.fehler ? entwurf : aktion.status.zustand !== 'ruhig' ? aktion.status : entwurf;
  return (
    <tr className={`border-t align-top ${neu ? 'bg-muted/20' : ''}`} {...entwurf.huelle}>
      <td className="px-2 py-2">
        <input
          value={field.name}
          onChange={e => updateField({ name: e.target.value })}
          placeholder="kostenstelle"
          aria-invalid={entwurf.fehler ? true : undefined}
          className="w-full border rounded px-2 py-1 font-mono text-xs"
        />
        <FeldStatus status={status} zeichen={false} />
      </td>
      <td className="px-2 py-2">
        <input
          value={field.label ?? ''}
          onChange={e => updateField({ label: e.target.value || undefined })}
          placeholder="Kostenstelle"
          className="w-full border rounded px-2 py-1 text-xs"
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={field.type}
          onChange={e => updateField({ type: e.target.value as CustomFieldType })}
          className="w-full border rounded px-2 py-1 text-xs"
        >
          {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={!!field.required}
          onChange={e => updateField({ required: e.target.checked })}
        />
      </td>
      <td className="px-2 py-2">
        <ConstraintsEditor field={field} onChange={updateField} />
      </td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        <span className="mr-1 inline-block align-middle"><FeldStatusZeichen status={status} /></span>
        {!neu && (
          <>
            <button
              type="button"
              onClick={() => onMove?.(-1)}
              disabled={isFirst}
              className="text-xs border rounded px-1 py-1 mr-1 disabled:opacity-30"
              aria-label="Nach oben"
            >↑</button>
            <button
              type="button"
              onClick={() => onMove?.(+1)}
              disabled={isLast}
              className="text-xs border rounded px-1 py-1 mr-1 disabled:opacity-30"
              aria-label="Nach unten"
            >↓</button>
          </>
        )}
        <button
          type="button"
          onClick={() => void removeField()}
          className="text-xs border border-red-200 text-red-700 rounded px-2 py-1 hover:bg-red-50"
        >{neu ? 'Verwerfen' : 'Entfernen'}</button>
      </td>
    </tr>
  );
}

// ─── Typspezifische Einschränkungen ───────────────────────────────────────

function ConstraintsEditor({
  field,
  onChange,
}: {
  field: CustomFieldSpec;
  onChange: (patch: Partial<CustomFieldSpec>) => void;
}) {
  if (field.type === 'string') {
    return (
      <div className="flex flex-wrap gap-2 text-xs">
        <label className="inline-flex items-center gap-1">
          maxLength
          <input
            type="number"
            value={field.maxLength ?? ''}
            onChange={e => onChange({ maxLength: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="w-16 border rounded px-1 py-0.5"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          Pattern (Regex)
          <input
            value={field.pattern ?? ''}
            onChange={e => onChange({ pattern: e.target.value || undefined })}
            placeholder="^[A-Z]{3}-[0-9]+$"
            className="w-48 border rounded px-1 py-0.5 font-mono"
          />
        </label>
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div className="flex flex-wrap gap-2 text-xs">
        <label className="inline-flex items-center gap-1">
          min
          <input
            type="number"
            value={field.min ?? ''}
            onChange={e => onChange({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="w-16 border rounded px-1 py-0.5"
          />
        </label>
        <label className="inline-flex items-center gap-1">
          max
          <input
            type="number"
            value={field.max ?? ''}
            onChange={e => onChange({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="w-16 border rounded px-1 py-0.5"
          />
        </label>
      </div>
    );
  }
  if (field.type === 'enum') {
    const joined = (field.options ?? []).join(', ');
    return (
      <label className="inline-flex items-center gap-1 text-xs w-full">
        Optionen (Komma-getrennt)
        <input
          value={joined}
          onChange={e =>
            onChange({
              options: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
            })
          }
          placeholder="niedrig, mittel, hoch"
          className="flex-1 border rounded px-1 py-0.5"
        />
      </label>
    );
  }
  return <span className="text-xs text-muted-foreground">–</span>;
}
